import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { type BoxWaiverFormFields, getBoxWaiverSchema } from "@modules/box/boxSettingsFormikType"
import { useCurrentBox } from "@modules/box/hooks/useBox"
import { useBoxSettingsActions } from "@modules/box/hooks/useBoxSettingsActions"
import { word } from "@modules/core/utils/i18n"
import { useState } from "react"

export const useBoxWaiverForm = () => {
  const [displayFileNameInput, setDisplayFileNameInput] = useState(false)
  const { result: box } = useCurrentBox()
  const { updateBoxWaiver, isUpdatingWaiver } = useBoxSettingsActions()

  const form = useAppForm({
    // Reinitialize once the box is loaded / updated (equivalent to Formik's enableReinitialize)
    defaultValues: box?.getBoxWaiverDraft() ?? ({ waiver: null } satisfies BoxWaiverFormFields),
    validators: {
      onBlur: getBoxWaiverSchema(),
      onSubmit: getBoxWaiverSchema(),
    },
    onSubmit: async ({ value }) => {
      if (!box) {
        toast.error(word("global.error.label"), {
          description: word("global.fileUpload.error"),
          classNames: errorToastClassName(),
        })
        throw new Error(word("box.form.settings.toast.error_box_undefined"))
      }

      // The filename is edited without its extension (validation forbids the ".") — append it on save.
      const waiver = value.waiver
        ? { ...value.waiver, filename: `${(value.waiver.filename ?? "").replace(/\.pdf$/i, "")}.pdf` }
        : null

      try {
        await updateBoxWaiver({ boxId: box.id, waiver })
        toast.success(word("box.form.settings.toast.success"), {
          classNames: successToastClassName(),
        })
        setDisplayFileNameInput(false)
      } catch (error) {
        console.error("Error updating box settings:", error)
        toast.error(word("box.form.settings.toast.error"), {
          description: error instanceof Error ? error.message : undefined,
          classNames: errorToastClassName(),
        })
      }
    },
  })

  return { form, isSubmitting: isUpdatingWaiver, displayFileNameInput, setDisplayFileNameInput }
}
